((root, factory) => {
  const api = factory();

  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  }

  if (root) {
    root.TodayMerchSearchQuery = api;
  }
})(typeof globalThis !== 'undefined' ? globalThis : this, () => {
  const tokenNormalizations = Object.freeze({
    agendas: 'agenda',
    backpacks: 'backpack',
    bonnets: 'bonnet',
    bouteilles: 'bouteille',
    bouteile: 'bouteille',
    bouteiles: 'bouteille',
    bureaux: 'bureau',
    cadeaux: 'cadeau',
    caps: 'cap',
    carnets: 'carnet',
    casquettes: 'casquette',
    casquete: 'casquette',
    casquetes: 'casquette',
    chemises: 'chemise',
    cups: 'cup',
    durables: 'durable',
    ecologiques: 'ecologique',
    écologiques: 'écologique',
    etanches: 'etanche',
    étanches: 'étanche',
    gobelets: 'gobelet',
    gourdes: 'gourde',
    gourrde: 'gourde',
    gourrdes: 'gourde',
    hoodies: 'hoodie',
    hoody: 'hoodie',
    inoxydables: 'inoxydable',
    isothermes: 'isotherme',
    jeux: 'jeu',
    lunchboxes: 'lunchbox',
    mugs: 'mug',
    notebooks: 'notebook',
    pochettes: 'pochette',
    polos: 'polo',
    pliables: 'pliable',
    publicitaires: 'publicitaire',
    recycles: 'recycle',
    recyclés: 'recyclé',
    reutilisables: 'reutilisable',
    réutilisables: 'réutilisable',
    sacs: 'sac',
    sacados: 'sac à dos',
    sweatshirts: 'sweatshirt',
    sweatshrit: 'sweatshirt',
    sweatshrits: 'sweatshirt',
    tabliers: 'tablier',
    tasses: 'tasse',
    thermiques: 'thermique',
    't-shirts': 't-shirt',
    't-shrit': 't-shirt',
    't-shrits': 't-shirt',
    'tee-shirt': 't-shirt',
    'tee-shirts': 't-shirt',
    tshirt: 't-shirt',
    tshirts: 't-shirt',
    tshrit: 't-shirt',
    tshrits: 't-shirt',
    totebags: 'totebag',
    trousses: 'trousse',
    tumblers: 'tumbler',
    vestes: 'veste',
    vetements: 'vetement',
    vêtements: 'vêtement',
  });

  const ignoredIntentTokens = new Set([
    'custom',
    'personnalisable',
    'personnalisables',
    'personnalise',
    'personnalises',
    'personnalisé',
    'personnalisée',
    'personnalisées',
    'personnalisés',
  ]);

  // Only replace complete product words or phrases. Punctuation used inside
  // supplier references (CAP-123, CUP.01, notebook/pro) is deliberately excluded.
  function phrasePattern(source) {
    return new RegExp(`(^|[^\\p{L}\\p{N}'’_./-])(?:${source})(?=$|[^\\p{L}\\p{N}'’_./-])`, 'giu');
  }

  const spellingPhrases = Object.freeze([
    [phrasePattern('(?:t|tee)[\\s-]+shirts?'), 't-shirt'],
    [phrasePattern('sacs?\\s+a\\s+dos'), 'sac à dos'],
  ]);

  const catalogAliases = Object.freeze([
    [phrasePattern('tote[\\s-]+bags?'), 'totebag'],
    [phrasePattern('backpacks?'), 'sac à dos'],
    [phrasePattern('hood(?:ie|ies|y)'), 'sweat à capuche'],
    [phrasePattern('sweatshirts?'), 'sweat'],
    [phrasePattern('notebooks?'), 'carnet'],
    [phrasePattern('tumblers?'), 'gobelet'],
    [phrasePattern('cups?'), 'gobelet'],
    [phrasePattern('gourdes?'), 'bouteille'],
    [phrasePattern('caps?'), 'casquette'],
  ]);

  function replacePhrases(query, phrases) {
    return phrases.reduce(
      (result, [pattern, replacement]) => result.replace(pattern, (_, prefix) => `${prefix}${replacement}`),
      query,
    );
  }

  function cleanQuery(query) {
    return String(query || '')
      .replace(/[’]/gu, "'")
      .replace(/\s+/gu, ' ')
      .trim();
  }

  function normalizeToken(token) {
    const match = token.match(/^([^\p{L}\p{N}]*)([\p{L}\p{N}'-]+)([^\p{L}\p{N}]*)$/u);
    if (!match) return token.toLocaleLowerCase('fr');

    const [, prefix, word, suffix] = match;
    const normalizedWord = word.toLocaleLowerCase('fr');
    if (ignoredIntentTokens.has(normalizedWord)) return '';

    return `${prefix}${tokenNormalizations[normalizedWord] || normalizedWord}${suffix}`;
  }

  function normalizeFrenchQuery(query) {
    return replacePhrases(cleanQuery(query), spellingPhrases)
      .split(' ')
      .map(normalizeToken)
      .filter(Boolean)
      .join(' ');
  }

  function applyCatalogAliases(query) {
    return cleanQuery(replacePhrases(cleanQuery(query).toLocaleLowerCase('fr'), catalogAliases));
  }

  function buildSearchCandidates(query) {
    const exactQuery = cleanQuery(query);
    if (!exactQuery) return [];

    const normalizedQuery = normalizeFrenchQuery(exactQuery);
    const aliasedQuery = applyCatalogAliases(normalizedQuery || exactQuery);
    const candidates = [];
    const seenCandidates = new Set();

    for (const candidate of [exactQuery, normalizedQuery, aliasedQuery]) {
      const cleanCandidate = cleanQuery(candidate);
      const candidateKey = cleanCandidate.toLocaleLowerCase('fr');
      if (!cleanCandidate || seenCandidates.has(candidateKey)) continue;

      candidates.push(cleanCandidate);
      seenCandidates.add(candidateKey);
    }

    return candidates.slice(0, 3);
  }

  return Object.freeze({
    applyCatalogAliases,
    buildSearchCandidates,
    cleanQuery,
    normalizeFrenchQuery,
  });
});
